import { compose } from 'redux';
import { connect } from 'react-redux';
import TRCReadingView from './TRCReadingView';
import AvatarView from '../../../common/avatar/avatarView';
import withAvatar from '../../../common/avatar/withAvatar';
import { playAvatarAudio } from '../../../common/avatar/avatarActions';
import { getTrcInstruction, getTrcCaption } from '../../troConstants';
import { setCaption } from '../../../common/audio/audioActions';
import {
    fetchTRCContent,
    resetActivePage,
    createTRCResult,
    goToNextPage,
    setReadingComplete,
    setLastRecordedPage,
    saveRecording,
    cleanUpBook,
} from '../../trcActions';
import {
    trackStartTroReading,
    trackStartTroPageRecording,
    trackStopTroPageRecording,
    trackCompleteTroPageRecording,
    trackCompleteTroBookRecording,
    trackCompleteTroReading,
    trackTroReadingNoButton,
    trackPressedRestartButton,
    trackFailedEnableMic,
    trackViewedTroBook,
    trackRecordedTroBook,
    trackReadTroBook,
    trackTroVrPopup,
    trackTroReadingNavigationButton,
} from '../../trcReporting';
import selectors from '../../../rootSelectors';
import {
    unblockScreen,
    unmountScreen,
    disableGoBackButton,
    enableGoBackButton,
    setConfirmPopupVisibility,
    showHelpPopup,
    hideHelpPopup,
    showSpinner,
    setShouldDisplayCaption,
} from '../../../common/screen/screenActions';
import { TRC, TRO } from '../../../constants/measure';
import { goToQuestion, goToStartPage } from '../../../common/navigation/navigationActions';
import { logUserOut } from '../../../common/user/userActions';
import {
    startTimeTracking,
    stopTimeTracking,
    saveTimeTrackingData,
} from '../../../common/timeTracking/timeTrackingActions';
import { TRO_PAGE_RECORDING, TRO_READING } from '../../../common/timeTracking/timeTrackingKeys';
import {
    clearRecordingStream,
    setRecordedResult,
} from '../../../common/recording/recordingActions';
import {
    executeApiCallFailedEffect,
    executeApiCallSucceededEffect,
    setStudentResultToResume,
} from '../../../common/api/trcoActions';

const bookCompleteReminderInstruction = 'bookCompleteReminder';

const mapStateToProps = state => ({
    isScreenBlocked: selectors.getIsScreenBlocked(state),
    isDataFetched: selectors.getIsTRCDataFetched(state),
    pageNum: selectors.getActiveTRCPage(state),
    pages: selectors.getTRCPages(state),
    isBackButtonActive: selectors.getIsBackButtonActive(state),
    activePage: selectors.getActiveTRCPage(state),
    isFirstTRCPage: selectors.isFirstTRCPage(state),
    screenLevelErrorStatus: selectors.getScreenLevelErrorStatus(state),
    isConfirmPopupActive: selectors.isConfirmPopupActive(state),
    isLastTRCPage: selectors.isLastTRCPage(state),
    indented: selectors.getIsBookIndented(state),
    isRecordingInProgress: selectors.getIsRecordingInProgress(state),
    recordedResult: selectors.getRecordedResult(state),
    isRecordingSaved: selectors.getIsRecordingSaved(state),
    isHelpPopupVisible: selectors.getIsHelpPopupVisible(state),
    resultsVRStatuses: selectors.getVRResultsStatus(state),
    isSpinnerVisible: selectors.getIsSpinnerVisible(state),
    resultVRErrorReasons: selectors.getVRErrorReasons(state),
    didApiCallSucceed: selectors.getDidApiCallSucceed(state),
    didApiCallFail: selectors.getDidApiCallFail(state),
    prevApiRequest: selectors.getPrevApiRequest(state),
    bookId: selectors.getTRCBookId(state),
    bookTitle: selectors.getTRCBookTitle(state),
    currentResult: selectors.findResultFor(state, { measure: TRO.MEASURE, isComplete: false }),
    recordingStream: selectors.getRecordingStream(state),
    hasUnscoredFailedResult: selectors.getHasUnscoredFailedResult(state),
    micPopupProblem: selectors.getRecordingError(state),
    isExternalURLSourcePresent: selectors.getExternalURLSourceStatus(state),
    arLanguage: selectors.getArLanguage(state),
    isBenchmarkModeEnabled: selectors.getisBenchmarkModeEnabled(state),
});

const mapDispatchToProps = dispatch => ({
    fetchContent: () => {
        return dispatch(fetchTRCContent());
    },
    initializeResultId: () =>
        dispatch(createTRCResult()).then(() => {
            dispatch(unblockScreen());
        }),
    onNext: () => {
        dispatch(goToNextPage());
    },
    onFinish: (isExternalURLSourcePresent, onInstructionAudioEnd, arLanguage) => {
        if (isExternalURLSourcePresent) {
            const instruction = getTrcInstruction(
                isExternalURLSourcePresent,
                bookCompleteReminderInstruction,
                arLanguage,
            );
            const caption = getTrcCaption(
                isExternalURLSourcePresent,
                bookCompleteReminderInstruction,
                arLanguage,
            );
            if (isExternalURLSourcePresent) {
                dispatch(setCaption(caption));
                dispatch(setShouldDisplayCaption(true));
            }
            dispatch(playAvatarAudio(instruction)).then(() => {
                if (isExternalURLSourcePresent) {
                    dispatch(setShouldDisplayCaption(false));
                }
                onInstructionAudioEnd();
            });
        }
        dispatch(setConfirmPopupVisibility(true));
    },
    completeReading: () => {
        dispatch(setReadingComplete());
        dispatch(stopTimeTracking(TRO_READING));
        dispatch(saveTimeTrackingData(TRO_READING));
        dispatch(setConfirmPopupVisibility(false));
    },
    goToNextPage: () => {
        dispatch(stopTimeTracking(TRO_READING));
        dispatch(setReadingComplete());
        dispatch(setConfirmPopupVisibility(false));
        dispatch(unblockScreen());
        dispatch(goToQuestion(TRC, null, 1));
    },
    returnToBook: () => {
        dispatch(setConfirmPopupVisibility(false));
    },
    onBack: page => {
        dispatch(trackTroReadingNavigationButton(page, 'back-button'));
        dispatch(goToNextPage(-1));
    },
    setBackButtonActive: isActive => {
        if (isActive) {
            dispatch(enableGoBackButton());
        } else {
            dispatch(disableGoBackButton());
        }
    },
    cleanup: () => {
        dispatch(resetActivePage());
        dispatch(unmountScreen());
    },
    showHelpPopup: () => {
        dispatch(showHelpPopup());
    },
    hideHelpPopup: () => {
        dispatch(hideHelpPopup());
    },
    showSpinner: () => {
        dispatch(showSpinner());
    },
    startTimeTracking: timeTrackingKey => dispatch(startTimeTracking(timeTrackingKey)),
    stopTimeTracking: timeTrackingKey => dispatch(stopTimeTracking(timeTrackingKey)),
    logUserOut,
    saveRecordedResult: async recordedResult => {
        dispatch(setLastRecordedPage());
        if (window.amplify_lantern_config.isVRTestMode && window.d394cd2151.ff350510a0515) {
            // if there's mock recording in memory, use it

            recordedResult = window.d394cd2151.ff350510a0515;
        }
        await dispatch(setRecordedResult(recordedResult));
        dispatch(saveRecording());
    },
    executeApiCallSucceededEffect: (prevApiRequest, resultId, bookId) =>
        dispatch(
            executeApiCallSucceededEffect({
                prevApiRequest,
                resultId,
                bookId,
            }),
        ),
    executeApiCallFailedEffect: (prevApiRequest, resultId, bookId) =>
        dispatch(
            executeApiCallFailedEffect({
                prevApiRequest,
                resultId,
                bookId,
            }),
        ),
    goToStart: isExternalURLSourcePresent => {
        if (!isExternalURLSourcePresent) dispatch(trackPressedRestartButton());
        dispatch(cleanUpBook());
        dispatch(clearRecordingStream());
        dispatch(setStudentResultToResume());
        dispatch(goToStartPage());
    },
    startPageRecordingTimeTracking: () => dispatch(startTimeTracking(TRO_PAGE_RECORDING)),
    stopPageRecordingTimeTracking: () => dispatch(stopTimeTracking(TRO_PAGE_RECORDING)),
    trackStartTroReading: () => dispatch(trackStartTroReading()),
    trackStartTroPageRecording: () => dispatch(trackStartTroPageRecording()),
    trackStopTroPageRecording: () => dispatch(trackStopTroPageRecording()),
    trackCompleteTroPageRecording: () => dispatch(trackCompleteTroPageRecording()),
    trackCompleteTroBookRecording: () => dispatch(trackCompleteTroBookRecording()),
    trackCompleteTroReading: () => dispatch(trackCompleteTroReading()),
    trackTroReadingNoButton: () => dispatch(trackTroReadingNoButton()),
    trackTroReadingNavigationButton: (page, buttonType) =>
        dispatch(trackTroReadingNavigationButton(page, buttonType)),
    trackViewedTroBook: () => dispatch(trackViewedTroBook()),
    trackRecordedTroBook: () => dispatch(trackRecordedTroBook()),
    trackReadTroBook: () => dispatch(trackReadTroBook()),
    trackTroVrPopup: problemText => dispatch(trackTroVrPopup(problemText)),
    trackFailedEnableMic: () => dispatch(trackFailedEnableMic()),
});

const avatarMapStateToProps = () => ({
    position: AvatarView.Position.LOWER_LEFT,
});

const TRCReadingContainer = compose(
    connect(mapStateToProps, mapDispatchToProps),
    withAvatar(avatarMapStateToProps),
)(TRCReadingView);

export default TRCReadingContainer;
